import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaDollarSign, FaPercent, FaCalculator } from 'react-icons/fa';
import StripeNavbar from './StripeNavbar';

export default function TeacherPricing() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => {
    const cookieLang = document.cookie.match(/taealam_language=([^;]+)/);
    return cookieLang ? cookieLang[1] : 'en';
  });
  const [hourlyRate, setHourlyRate] = useState(10);
  const [totalHours, setTotalHours] = useState(1);
  const isArabic = lang === 'ar';
  const isMobile = window.innerWidth <= 768;

  const calculateEarnings = (rate, hours = 1) => {
    const fixedFee = 0.5;
    const commission = 0.15; // 15%
    const sessionTotal = rate * hours;
    const totalFixedFee = fixedFee * hours;
    const commissionAmount = sessionTotal * commission;
    const totalFee = totalFixedFee + commissionAmount;
    const teacherEarnings = sessionTotal - totalFee;
    return {
      hourlyRate: rate,
      totalHours: hours,
      sessionTotal,
      fixedFee,
      totalFixedFee,
      commissionAmount,
      totalFee,
      teacherEarnings,
      percentage: rate > 0 ? ((teacherEarnings / sessionTotal) * 100).toFixed(1) : 0
    };
  };

  const calculation = calculateEarnings(hourlyRate, totalHours);

  return (
    <div dir={isArabic ? 'rtl' : 'ltr'} style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <StripeNavbar lang={lang} setLang={setLang} alwaysScrolled={true} />
      
      <div style={{
        maxWidth: 1000,
        margin: '0 auto',
        padding: isMobile ? '100px 20px 60px' : '120px 40px 80px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
            marginBottom: 24,
            color: 'white'
          }}>
            <FaDollarSign size={32} />
          </div>
          <h1 style={{
            fontSize: isMobile ? 32 : 42,
            fontWeight: 800,
            color: '#0F172A',
            marginBottom: 12,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'الأسعار والعمولات' : 'Pricing & Commission'}
          </h1>
          <p style={{
            fontSize: 18,
            color: '#64748B',
            maxWidth: 600,
            margin: '0 auto',
            lineHeight: 1.6,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic 
              ? 'تعرف على هيكل الأسعار والعمولات لمنصة تعلّم'
              : 'Learn about Taealam\'s pricing structure and commission rates'}
          </p>
        </div>

        {/* Pricing Structure */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: 20,
          padding: isMobile ? '32px 24px' : '48px',
          marginBottom: 40,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E2E8F0'
        }}>
          <h2 style={{
            fontSize: 28,
            fontWeight: 700,
            color: '#0F172A',
            marginBottom: 24,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'هيكل الرسوم' : 'Fee Structure'}
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 24,
            marginBottom: 32
          }}>
            <div style={{
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(255, 116, 38, 0.1) 0%, rgba(255, 116, 38, 0.05) 100%)',
              borderRadius: 16,
              border: '1px solid rgba(255, 116, 38, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12
              }}>
                <FaDollarSign size={24} color="#FF7426" />
                <h3 style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#0F172A',
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {isArabic ? 'رسوم ثابتة' : 'Fixed Fee'}
                </h3>
              </div>
              <p style={{
                fontSize: 32,
                fontWeight: 800,
                color: '#FF7426',
                margin: '8px 0',
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}>
                0.5 BD
              </p>
              <p style={{
                fontSize: 14,
                color: '#64748B',
                margin: 0,
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}>
                {isArabic 
                  ? 'رسوم ثابتة لكل جلسة'
                  : 'Fixed fee per session'}
              </p>
            </div>

            <div style={{
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(255, 116, 38, 0.1) 0%, rgba(255, 116, 38, 0.05) 100%)',
              borderRadius: 16,
              border: '1px solid rgba(255, 116, 38, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12
              }}>
                <FaPercent size={24} color="#FF7426" />
                <h3 style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#0F172A',
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {isArabic ? 'عمولة' : 'Commission'}
                </h3>
              </div>
              <p style={{
                fontSize: 32,
                fontWeight: 800,
                color: '#FF7426',
                margin: '8px 0',
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}>
                15%
              </p>
              <p style={{
                fontSize: 14,
                color: '#64748B',
                margin: 0,
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}>
                {isArabic 
                  ? 'من سعر الجلسة بالساعة'
                  : 'Of hourly session rate'}
              </p>
            </div>
          </div>

          <div style={{
            padding: '24px',
            background: '#F8FAFC',
            borderRadius: 16,
            border: '1px solid #E2E8F0'
          }}>
            <h3 style={{
              fontSize: 18,
              fontWeight: 600,
              color: '#0F172A',
              marginBottom: 16,
              fontFamily: "'IBM Plex Sans Arabic', sans-serif"
            }}>
              {isArabic ? 'كيف يتم حساب الرسوم؟' : 'How are fees calculated?'}
            </h3>
            <p style={{
              fontSize: 16,
              color: '#64748B',
              lineHeight: 1.7,
              marginBottom: 16,
              fontFamily: "'IBM Plex Sans Arabic', sans-serif"
            }}>
              {isArabic 
                ? 'الرسوم الإجمالية = 0.5 دينار بحريني (رسوم ثابتة) + 15% من سعر الجلسة بالساعة'
                : 'Total Fee = 0.5 BD (Fixed Fee) + 15% of hourly session rate'}
            </p>
            <p style={{
              fontSize: 16,
              color: '#64748B',
              lineHeight: 1.7,
              margin: 0,
              fontFamily: "'IBM Plex Sans Arabic', sans-serif"
            }}>
              {isArabic 
                ? 'أرباحك = سعر الجلسة - الرسوم الإجمالية'
                : 'Your Earnings = Session Rate - Total Fee'}
            </p>
          </div>
        </div>

        {/* Calculator */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: 20,
          padding: isMobile ? '32px 24px' : '48px',
          marginBottom: 40,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E2E8F0'
        }}>
          <h2 style={{
            fontSize: 28,
            fontWeight: 700,
            color: '#0F172A',
            marginBottom: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif",
            ...(isArabic ? { flexDirection: 'row-reverse' } : {})
          }}>
            <FaCalculator size={28} color="#FF7426" />
            {isArabic ? 'حاسبة الأرباح' : 'Earnings Calculator'}
          </h2>

          {/* Range Sliders */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 32,
            marginBottom: 32
          }}>
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12
              }}>
                <label style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#0F172A',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {isArabic ? 'السعر بالساعة (دينار بحريني)' : 'Price Per Hour (BD)'}
                </label>
                <span style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#FF7426',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {hourlyRate} BD
                </span>
              </div>
              <div style={{ position: 'relative', padding: '8px 0' }}>
                <input
                  type="range"
                  min="1"
                  max="20"
                  step="0.5"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    height: 8,
                    borderRadius: 4,
                    background: '#E2E8F0',
                    outline: 'none',
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    margin: 0
                  }}
                />
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: '#94A3B8',
                marginTop: 8
              }}>
                <span>1 BD</span>
                <span>20 BD</span>
              </div>
            </div>
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12
              }}>
                <label style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#0F172A',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {isArabic ? 'إجمالي الساعات' : 'Total Hours'}
                </label>
                <span style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#FF7426',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {totalHours} {isArabic ? 'ساعة' : totalHours === 1 ? 'hour' : 'hours'}
                </span>
              </div>
              <div style={{ position: 'relative', padding: '8px 0' }}>
                <input
                  type="range"
                  min="1"
                  max="6"
                  step="1"
                  value={totalHours}
                  onChange={(e) => setTotalHours(parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    height: 8,
                    borderRadius: 4,
                    background: '#E2E8F0',
                    outline: 'none',
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    margin: 0
                  }}
                />
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: '#94A3B8',
                marginTop: 8
              }}>
                <span>1 {isArabic ? 'ساعة' : 'hour'}</span>
                <span>6 {isArabic ? 'ساعات' : 'hours'}</span>
              </div>
            </div>
          </div>
          
          <style>{`
            input[type="range"] {
              -webkit-appearance: none;
              appearance: none;
              background: transparent;
              cursor: pointer;
            }
            
            input[type="range"]::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 24px;
              height: 24px;
              border-radius: 50%;
              background: linear-gradient(135deg, #FF7426 0%, #FF9854 100%);
              cursor: pointer;
              box-shadow: 0 2px 8px rgba(255, 116, 38, 0.4);
              border: 3px solid #FFFFFF;
              transition: transform 0.2s ease;
              margin-top: -8px;
            }
            
            input[type="range"]::-webkit-slider-thumb:hover {
              transform: scale(1.1);
              box-shadow: 0 4px 12px rgba(255, 116, 38, 0.6);
            }
            
            input[type="range"]::-moz-range-thumb {
              width: 24px;
              height: 24px;
              border-radius: 50%;
              background: linear-gradient(135deg, #FF7426 0%, #FF9854 100%);
              cursor: pointer;
              border: 3px solid #FFFFFF;
              box-shadow: 0 2px 8px rgba(255, 116, 38, 0.4);
              transition: transform 0.2s ease;
            }
            
            input[type="range"]::-moz-range-thumb:hover {
              transform: scale(1.1);
              box-shadow: 0 4px 12px rgba(255, 116, 38, 0.6);
            }
            
            input[type="range"]::-webkit-slider-runnable-track {
              height: 8px;
              background: #E2E8F0;
              border-radius: 4px;
            }
            
            input[type="range"]::-moz-range-track {
              height: 8px;
              background: #E2E8F0;
              border-radius: 4px;
            }
            
            input[type="range"]:focus {
              outline: none;
            }
            
            input[type="range"]:focus::-webkit-slider-thumb {
              box-shadow: 0 0 0 4px rgba(255, 116, 38, 0.2);
            }
            
            input[type="range"]:focus::-moz-range-thumb {
              box-shadow: 0 0 0 4px rgba(255, 116, 38, 0.2);
            }
          `}</style>

          {/* Calculation Results */}
          <div style={{
            padding: '32px',
            background: 'linear-gradient(135deg, rgba(255, 116, 38, 0.1) 0%, rgba(255, 116, 38, 0.05) 100%)',
            borderRadius: 16,
            border: '1px solid rgba(255, 116, 38, 0.2)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 24,
              marginBottom: 24
            }}>
              <div>
                <p style={{
                  fontSize: 14,
                  color: '#64748B',
                  margin: '0 0 8px 0',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {isArabic ? 'إجمالي قيمة الجلسات' : 'Total Session Value'}
                </p>
                <p style={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: '#0F172A',
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {calculation.sessionTotal.toFixed(2)} BD
                </p>
                <p style={{
                  fontSize: 12,
                  color: '#64748B',
                  margin: '4px 0 0 0',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  ({calculation.hourlyRate} BD × {calculation.totalHours} {isArabic ? 'ساعة' : 'hours'})
                </p>
              </div>
              <div>
                <p style={{
                  fontSize: 14,
                  color: '#64748B',
                  margin: '0 0 8px 0',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {isArabic ? 'أرباحك الإجمالية' : 'Your Total Earnings'}
                </p>
                <p style={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: '#10B981',
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {calculation.teacherEarnings.toFixed(2)} BD
                </p>
                <p style={{
                  fontSize: 12,
                  color: '#64748B',
                  margin: '4px 0 0 0',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  ({calculation.percentage}% {isArabic ? 'من الإجمالي' : 'of total'})
                </p>
              </div>
            </div>

            {/* Breakdown */}
            <div style={{
              marginTop: 24,
              paddingTop: 24,
              borderTop: '1px solid rgba(255, 116, 38, 0.2)'
            }}>
              <h3 style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#0F172A',
                marginBottom: 16,
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}>
                {isArabic ? 'تفاصيل الرسوم' : 'Fee Breakdown'}
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{
                    fontSize: 14,
                    color: '#64748B',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                  }}>
                    {isArabic ? 'رسوم ثابتة' : 'Fixed Fee'} ({calculation.totalHours} {isArabic ? 'جلسة' : calculation.totalHours === 1 ? 'session' : 'sessions'} × 0.5 BD):
                  </span>
                  <span style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#0F172A',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                  }}>
                    {calculation.totalFixedFee.toFixed(2)} BD
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{
                    fontSize: 14,
                    color: '#64748B',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                  }}>
                    {isArabic ? 'عمولة' : 'Commission'} (15%):
                  </span>
                  <span style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#0F172A',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                  }}>
                    {calculation.commissionAmount.toFixed(2)} BD
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: 12,
                  borderTop: '1px solid rgba(255, 116, 38, 0.2)',
                  marginTop: 4
                }}>
                  <span style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#0F172A',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                  }}>
                    {isArabic ? 'إجمالي الرسوم' : 'Total Fees'}:
                  </span>
                  <span style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: '#FF7426',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                  }}>
                    {calculation.totalFee.toFixed(2)} BD
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div style={{
          background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
          borderRadius: 20,
          padding: isMobile ? '40px 24px' : '48px',
          color: 'white',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: 28,
            fontWeight: 700,
            marginBottom: 24,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'لماذا تعلّم؟' : 'Why Choose Taealam?'}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 24,
            marginTop: 32
          }}>
            {[
              {
                title: isArabic ? 'مدفوعات آمنة' : 'Secure Payments',
                desc: isArabic ? 'احصل على أموالك بأمان وفي الوقت المحدد' : 'Get paid securely and on time'
              },
              {
                title: isArabic ? 'منصة موثوقة' : 'Trusted Platform',
                desc: isArabic ? 'الآلاف من الطلاب يبحثون عن معلمين' : 'Thousands of students looking for teachers'
              },
              {
                title: isArabic ? 'دعم كامل' : 'Full Support',
                desc: isArabic ? 'فريق دعم متاح لمساعدتك' : 'Support team available to help you'
              }
            ].map((benefit, idx) => (
              <div key={idx}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: 20
                }}>
                  <FaCheck />
                </div>
                <h3 style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 8,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {benefit.title}
                </h3>
                <p style={{
                  fontSize: 14,
                  opacity: 0.9,
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                }}>
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

